import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Briefcase,
  Bug,
  Building2,
  Calculator,
  Car,
  Check,
  Eye,
  Flower2,
  GraduationCap,
  Hammer,
  House,
  PawPrint,
  Scale,
  Search,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smile,
  Sparkles,
  Stethoscope,
  Thermometer,
  TrendingUp,
  Truck,
  UtensilsCrossed,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Alert, Badge, Button, Input, Label } from '@buzzbee/ui';
import { useMemo, useState } from 'react';
import { del, errorMessage, put } from '../lib/api';

/** Icon names used by the template packs (packages/domain/src/packs/data.ts `icon`). */
const ICONS: Record<string, LucideIcon> = {
  Activity,
  Briefcase,
  Bug,
  Building2,
  Calculator,
  Car,
  Eye,
  Flower2,
  GraduationCap,
  Hammer,
  House,
  PawPrint,
  Scale,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smile,
  Sparkles,
  Stethoscope,
  Thermometer,
  TrendingUp,
  Truck,
  UtensilsCrossed,
  Users,
};

export type FlowUse =
  | 'basic_booking'
  | 'sms_confirmations'
  | 'reminders'
  | 'review_requests'
  | 'crm_sync'
  | 'order_status'
  | 'faq'
  | 'campaigns'
  | 'status_updates';
export type SuggestedFlow = { label: string; description: string; uses: FlowUse[] };
export type PackSummary = {
  id: string;
  name: string;
  icon: string;
  services: number;
  faq: number;
  guardrails: string[];
  books: boolean;
  defaultHours: Record<string, { opens: string; closes: string }[]>;
  suggestedFlows: SuggestedFlow[];
};

export function PackIcon({ name, className }: { name: string; className?: string }) {
  const Icon = ICONS[name] ?? Briefcase;
  return <Icon className={className} aria-hidden />;
}

/** Searchable grid of industries. Choosing one is what pre-fills the rest of setup. */
export function IndustryPicker({
  packs,
  value,
  onChange,
}: {
  packs: PackSummary[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? packs.filter((p) => p.name.toLowerCase().includes(q) || p.id.includes(q)) : packs;
  }, [packs, query]);
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          aria-label="Search industries"
          placeholder="Search industries, e.g. dental"
          className="pl-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div
        role="radiogroup"
        aria-label="Industry"
        className="grid max-h-80 gap-2 overflow-y-auto p-0.5 sm:grid-cols-2"
      >
        {shown.map((p) => {
          const on = p.id === value;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(p.id)}
              className={`flex items-center gap-3 rounded-xl p-3 text-left ring-1 transition-colors ${on ? 'bg-primary/5 ring-2 ring-primary' : 'ring-border hover:bg-muted/60'}`}
            >
              <span
                className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${on ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}
              >
                <PackIcon name={p.icon} className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{p.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {p.books ? `${p.services} services · ${p.faq} FAQs` : `Order questions · ${p.faq} FAQs`}
                </span>
              </span>
              {on ? <Check className="size-4 shrink-0 text-primary" aria-hidden /> : null}
            </button>
          );
        })}
        {shown.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground sm:col-span-2">
            No industry matches. Pick "General appointments" and edit it to fit.
          </p>
        ) : null}
      </div>
    </div>
  );
}

const USE_LABELS: Record<FlowUse, { label: string; soon?: boolean }> = {
  basic_booking: { label: 'Booking' },
  sms_confirmations: { label: 'Text confirmations' },
  reminders: { label: 'Reminders' },
  review_requests: { label: 'Review requests' },
  crm_sync: { label: 'CRM sync' },
  order_status: { label: 'Order lookup' },
  faq: { label: 'FAQ answers' },
  campaigns: { label: 'Follow-up campaigns', soon: true },
  status_updates: { label: 'Status texts', soon: true },
};

export function SuggestedFlows({ flows }: { flows: SuggestedFlow[] }) {
  return (
    <ul className="space-y-2">
      {flows.map((f) => (
        <li key={f.label} className="rounded-lg bg-muted/50 p-3">
          <p className="text-sm font-medium">{f.label}</p>
          <p className="text-xs text-muted-foreground">{f.description}</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {f.uses.map((u) => (
              <Badge key={u} variant={USE_LABELS[u].soon ? 'default' : 'success'}>
                {USE_LABELS[u].label}
                {USE_LABELS[u].soon ? ' · coming soon' : ''}
              </Badge>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** What choosing this industry fills in for you. */
export function IndustryPreview({ pack }: { pack: PackSummary }) {
  const days = Object.keys(pack.defaultHours).length;
  return (
    <div className="space-y-3 rounded-xl p-4 ring-1 ring-border">
      <p className="text-sm font-medium">We'll pre-fill for {pack.name}:</p>
      <ul className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
        <li>{pack.books ? `${pack.services} services to book` : 'No booking: answers order questions'}</li>
        <li>{pack.faq} common questions and answers</li>
        <li>{days ? `Opening hours, ${days} days a week` : 'Opening hours'}</li>
        <li>
          {pack.guardrails.length
            ? `Won't advise on ${pack.guardrails.map((g) => g.replace(/_/g, ' ')).join(', ')}`
            : 'Standard safety rules'}
        </li>
      </ul>
      <div className="space-y-1.5">
        <p className="text-sm font-medium">Suggested flows</p>
        <SuggestedFlows flows={pack.suggestedFlows} />
      </div>
      <p className="text-xs text-muted-foreground">
        Every later step can be skipped, and you can change all of it in Settings.
      </p>
    </div>
  );
}

export type StoreConnection = {
  provider: 'shopify';
  shopDomain: string;
  tokenHint: string;
  status: string;
} | null;

/** Connect Shopify so the assistant can answer "where's my order" (e-commerce pack). */
export function StoreConnectionForm({ store }: { store: StoreConnection }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ shopDomain: store?.shopDomain ?? '', accessToken: '' });
  const [message, setMessage] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => put<{ message: string }>('/api/settings/store', form),
    onSuccess: async (r) => {
      setMessage(r.message);
      setForm((f) => ({ ...f, accessToken: '' }));
      await qc.invalidateQueries();
    },
  });
  const remove = useMutation({
    mutationFn: () => del('/api/settings/store'),
    onSuccess: () => qc.invalidateQueries(),
  });
  return (
    <div className="space-y-4">
      {store ? (
        <Alert variant="success" title={`Connected to ${store.shopDomain}`}>
          Access token ending in <span className="figures">{store.tokenHint}</span>. Paste a new token below
          to replace it.
        </Alert>
      ) : (
        <p className="text-sm text-muted-foreground">
          In Shopify, create a custom app (Settings, Apps and sales channels, Develop apps) with the{' '}
          <code>read_orders</code> scope, install it, and paste its Admin API access token here. Until then
          the assistant takes a message for order questions.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="shop-domain">Store domain</Label>
          <Input
            id="shop-domain"
            placeholder="my-store.myshopify.com"
            value={form.shopDomain}
            onChange={(e) => setForm({ ...form, shopDomain: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shop-token">Admin API access token</Label>
          <Input
            id="shop-token"
            type="password"
            autoComplete="off"
            placeholder="shpat_…"
            value={form.accessToken}
            onChange={(e) => setForm({ ...form, accessToken: e.target.value })}
          />
        </div>
      </div>
      {save.error ? <Alert variant="destructive">{errorMessage(save.error)}</Alert> : null}
      {message && !save.error ? <p className="text-sm text-muted-foreground">{message}</p> : null}
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!form.shopDomain || !form.accessToken}
        >
          {store ? 'Replace connection' : 'Connect Shopify'}
        </Button>
        {store ? (
          <Button type="button" variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
            Disconnect
          </Button>
        ) : null}
      </div>
    </div>
  );
}
